import React, { forwardRef, useEffect, useMemo } from 'react';
import { isNumber } from 'lodash';
import { Container } from '@fiverr-private/layout_components';
import { useElementSize, useHover } from '@fiverr-private/hooks';
import useScroll from '../../hooks/useScroll';
import { type CarouselProps } from './types';
import { CarouselContextProvider, DEFAULT_BREAKPOINTS } from './constants';
import { getChildren } from './utils';

const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      breakpoints = DEFAULT_BREAKPOINTS,
      scrollType = 'single',
      swipeModeDevice = 'tablet',
      isInfinite,
      scrollToSlideIndex,
      showSwipeIndicator,
      onScroll,
      children,
      ...restContainerProps
    }: CarouselProps,
    ref
  ) => {
    const { Slides, SlidesIndicator } = useMemo(() => getChildren(children), [children]);
    const { hovered, onMouseEnter, onMouseLeave } = useHover();

    const carouselContext = useScroll({
      scrollType,
      isInfinite,
      slides: Slides,
      breakpoints,
      withSlidesIndicator: !!SlidesIndicator,
      swipeModeDevice,
      showSwipeIndicator,
    });

    const {
      resetCarousel,
      canScrollLeft,
      canScrollRight,
      canScrollOnInfinite,
      currentPosition,
      checkTrackPosition,
      scrollToPosition,
      numOfMountedSlides,
    } = carouselContext;

    const { width: windowWidth, height: windowHeight } = useElementSize(typeof window !== 'undefined' ? window : null);
    const carouselDidMount = Slides.length === numOfMountedSlides;

    useEffect(() => {
      if (carouselDidMount) {
        resetCarousel();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth, windowHeight, carouselDidMount]);

    useEffect(() => {
      if (carouselDidMount) {
        onScroll?.({ canScrollLeft, canScrollRight, canScrollOnInfinite, currentPosition });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carouselDidMount, canScrollLeft, canScrollRight, canScrollOnInfinite, currentPosition]);

    useEffect(() => {
      checkTrackPosition();
    }, [Slides, checkTrackPosition]);

    useEffect(() => {
      if (carouselDidMount && isNumber(scrollToSlideIndex)) {
        scrollToPosition(scrollToSlideIndex);
      }
    }, [carouselDidMount, scrollToSlideIndex, scrollToPosition]);

    return (
      <CarouselContextProvider value={{ ...carouselContext, hovered }}>
        <Container position="relative" width="100%" {...restContainerProps} ref={ref}>
          <Container
            overflowX="hidden"
            ref={carouselContext.slidesListRef}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {children}
          </Container>
        </Container>
      </CarouselContextProvider>
    );
  }
);

Carousel.displayName = 'Carousel';
export default Carousel;
